import './cartItem.scss';

import React, { useCallback, useMemo } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import {
    useRemoveItemFromCartAction,
    useUpdateCartItemsAction,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/cart/actions';
import GatsbyImageWrapper from '@progymedia/sbi-web-occ/src/components/GatsbyImageWrapper';
import { Icon } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import LocalePrice from '../../utils/LocalePrice';
import QtyInput from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/QtyInput/QtyInput';
import { pushRemoveFromCartEvent } from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/useGTM_UA';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useTranslation } from 'react-i18next';

interface ICartItem {
    item: any;
    setLoading: any;
    editable?: boolean;
    className?: string;
}

export default function CartItem({
    item,
    setLoading,
    editable = true,
    className = 'full',
}: ICartItem) {
    const { t } = useTranslation('templatePages');

    const [updateCartItems] = useUpdateCartItemsAction();
    const [removeCartItem] = useRemoveItemFromCartAction();
    const { currentLocale } = usePageLocalization();

    const allProducts = useStaticQuery(
        graphql`
            query {
                allProductsJson {
                    nodes {
                        code
                        images
                        fields {
                            ...productsFieldsImagesFragment
                        }
                    }
                }
            }
        `
    );
    const products = allProducts.allProductsJson.nodes;
    const product = useMemo(
        () =>
            products.find((product: any) => product.code === item.product.sku),
        [item.product.sku, products]
    );

    const onItemRemoveClick = useCallback(
        async (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
            event.preventDefault();

            setLoading(true);
            pushRemoveFromCartEvent({
                id: item.product.sku,
                price: item.prices.price.value,
                currency: item.prices.price.currency,
                qty: item.quantity,
            });
            await removeCartItem(item.id);
            setLoading(false);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [item, item.id]
    );

    const updateQuantity = useCallback(
        async (newQuantity: any) => {
            try {
                setLoading(true);
                await updateCartItems(item.id, newQuantity);
                setLoading(false);
            } catch (error) {
                console.log(error);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [item, item.id]
    );

    return (
        <div className={`cartSingleItem ${className}`}>
            {product?.fields?.images && (
                <div className='qItemImg'>
                    <GatsbyImageWrapper fields={product?.fields} />
                </div>
            )}

            <div className='qItemGroup'>
                <div className='qItemInfo'>
                    <h4>{item.product.name}</h4>
                    <p className='code'>{item.product.sku}</p>

                    <p className='singleItemPrice'>
                        {LocalePrice(currentLocale, item.prices.price.value)}
                    </p>

                    <div
                        className='singleItemActions'
                        onClick={(e) => onItemRemoveClick(e)}
                    >
                        {t('delete')}
                    </div>
                </div>

                {editable && (
                    <div className='qItemQty'>
                        <QtyInput
                            qtyValue={item.quantity}
                            setQtyValue={updateQuantity}
                        />
                    </div>
                )}

                <div className='qItemTotal'>
                    {LocalePrice(currentLocale, item.prices.row_total.value)}
                </div>
            </div>

            {editable && (
                <div className='qItemRemove'>
                    <span onClick={(e) => onItemRemoveClick(e)}>
                        <Icon
                            d='m0 2.03l2-1.99 13.98 13.98 14.02-14.02 2 2-14.02 14.02 13.98 13.98-1.99 2-14.02-14.02-13.99 13.98-1.96-1.96 13.98-13.98z'
                            viewbox='0 0 32 32'
                            size='12'
                        />
                    </span>
                </div>
            )}
        </div>
    );
}
